import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { campaignAPI, messageAPI, contactAPI, whatsappAPI } from '../api';

function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState(null);
  const [failedMessages, setFailedMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('messages');
  const [whatsappStatus, setWhatsappStatus] = useState({ isConnected: false, state: 'disconnected' });

  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);

  const [messageForm, setMessageForm] = useState({ 
    content: '', 
    template_name: '', 
    media_url: '',
    sequence_order: 0,
    delay_hours: 0,
    template_id: ''
  });
  
  // Business API template support
  const [businessTemplates, setBusinessTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [useTemplate, setUseTemplate] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templatePlaceholders, setTemplatePlaceholders] = useState([]);
  const [placeholderValues, setPlaceholderValues] = useState({});
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [scheduledTime, setScheduledTime] = useState('');
  const [showNewContactsOnly, setShowNewContactsOnly] = useState(false);
  const [contactSearchText, setContactSearchText] = useState('');
  
  const [config, setConfig] = useState({
    messages_per_day: 100,
    delay_between_messages: 2000,
    start_time: '09:00',
    end_time: '18:00',
    excluded_days: '0',
    is_drip_campaign: 0,
    sequence_delays: '24,48,72'
  });

  useEffect(() => {
    loadCampaignData();
    loadWhatsAppStatus();
    
    // Refresh WhatsApp status every 5 seconds
    const interval = setInterval(loadWhatsAppStatus, 5000);
    return () => clearInterval(interval);
  }, [id]);

  const loadWhatsAppStatus = async () => {
    try {
      const response = await whatsappAPI.getStatus();
      setWhatsappStatus(response.data);
      
      // Load templates if Business API is active
      if (response.data.type === 'business_api' && response.data.hasApiKey) {
        loadBusinessTemplates();
      }
    } catch (error) {
      console.error('Error loading WhatsApp status:', error);
    }
  };
  
  const loadBusinessTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const response = await whatsappAPI.getBusinessTemplates({ limit: 100 });
      setBusinessTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };
  
  const handleTemplateSelect = (e) => {
    const templateId = e.target.value;
    const template = businessTemplates.find(t => t._id === templateId);
    
    if (template) {
      // Extract body text from components
      const bodyComponent = template.components?.find(c => c.type === 'BODY');
      const bodyText = bodyComponent?.text || '';
      
      // Extract placeholders ({{1}}, {{2}}, etc.)
      const placeholderRegex = /\{\{(\d+)\}\}/g;
      const placeholders = [];
      let match;
      while ((match = placeholderRegex.exec(bodyText)) !== null) {
        const num = parseInt(match[1]);
        if (!placeholders.includes(num)) {
          placeholders.push(num);
        }
      }
      placeholders.sort((a, b) => a - b);
      
      // Get example values if available
      const exampleValues = bodyComponent?.example?.body_text?.[0] || [];
      const initialValues = {};
      placeholders.forEach((num, index) => {
        initialValues[num] = exampleValues[index] || '';
      });
      
      setSelectedTemplate(template);
      setTemplatePlaceholders(placeholders);
      setPlaceholderValues(initialValues);
      setMessageForm({
        ...messageForm,
        template_id: templateId,
        template_name: template.name,
        content: bodyText
      });
    } else {
      // Clear template selection
      setSelectedTemplate(null);
      setTemplatePlaceholders([]);
      setPlaceholderValues({});
    }
  };

  const loadCampaignData = async () => {
    try {
      const [campaignRes, statsRes, allContactsRes, configRes] = await Promise.all([
        campaignAPI.getOne(id),
        campaignAPI.getStats(id),
        contactAPI.getAll(),
        campaignAPI.getConfig(id)
      ]);

      setCampaign(campaignRes.data);
      setMessages(campaignRes.data.messages || []);
      setContacts(campaignRes.data.contacts || []);
      setStats(statsRes.data);
      setAllContacts(allContactsRes.data);
      setConfig(configRes.data);
    } catch (error) {
      console.error('Error loading campaign:', error);
      setError('Failed to load campaign details');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMessage = async (e) => {
    e.preventDefault();
    try {
      // Include template placeholders if available
      const messageData = {
        ...messageForm,
        campaign_id: id,
        template_parameters: templatePlaceholders.length > 0 ? JSON.stringify(placeholderValues) : null
      };
      
      if (editingMessageId) {
        // Update existing message
        await messageAPI.update(editingMessageId, messageData);
        setSuccess('Message updated successfully');
        setEditingMessageId(null);
      } else {
        // Create new message
        await messageAPI.create(messageData);
        setSuccess('Message added successfully');
      }
      setShowMessageModal(false);
      setMessageForm({ content: '', template_name: '', media_url: '', sequence_order: 0, delay_hours: 0, template_id: '' });
      setSelectedTemplate(null);
      setTemplatePlaceholders([]);
      setPlaceholderValues({});
      loadCampaignData();
    } catch (error) {
      setError(editingMessageId ? 'Failed to update message' : 'Failed to add message');
    }
  };

  const handleEditMessage = (message) => {
    setEditingMessageId(message.id);
    setMessageForm({
      content: message.content,
      template_name: message.template_name || '',
      media_url: message.media_url || '',
      sequence_order: message.sequence_order || 0,
      delay_hours: message.delay_hours || 0
    });
    setShowMessageModal(true);
  };

  const handleCloseMessageModal = () => {
    setShowMessageModal(false);
    setEditingMessageId(null);
    setMessageForm({ content: '', template_name: '', media_url: '', sequence_order: 0, delay_hours: 0, template_id: '' });
    setUseTemplate(false);
    setSelectedTemplate(null);
    setTemplatePlaceholders([]);
    setPlaceholderValues({});
  };

  const handleAddContacts = async () => {
    try {
      await campaignAPI.addContacts(id, selectedContacts);
      setShowContactModal(false);
      setSelectedContacts([]);
      setContactSearchText('');
      setShowNewContactsOnly(false);
      setSuccess('Contacts added successfully');
      loadCampaignData();
    } catch (error) {
      setError('Failed to add contacts');
    }
  };

  const handleSendNow = async () => {
    if (window.confirm(`Send this campaign now? ${messages.length > 1 ? `\\n\\n${messages.length} message templates will be rotated across contacts.` : ''}`)) {
      try {
        const response = await whatsappAPI.sendCampaign(id);
        setSuccess(`✅ ${response.data.queued} messages queued with randomized delays${messages.length > 1 ? ` (${messages.length} templates rotating)` : ''}! Check Monitoring tab for progress.`);
        loadCampaignData();
      } catch (error) {
        setError('Failed to send campaign: ' + error.response?.data?.error || error.message);
      }
    }
  };

  const handleSchedule = async (e) => {
    e.preventDefault();
    try {
      await whatsappAPI.scheduleCampaign(id, scheduledTime);
      setShowScheduleModal(false);
      setScheduledTime('');
      setSuccess('Campaign scheduled successfully');
    } catch (error) {
      setError('Failed to schedule campaign');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (window.confirm('Delete this message?')) {
      try {
        await messageAPI.delete(messageId);
        setSuccess('Message deleted');
        loadCampaignData();
      } catch (error) {
        setError('Failed to delete message');
      }
    }
  };

  const handleUnpauseContact = async (phoneNumber, contactName) => {
    if (window.confirm(`Unpause messaging for ${contactName || phoneNumber}?\n\nThis will allow this contact to receive messages from this campaign again.`)) {
      try {
        await campaignAPI.unpauseContact(id, phoneNumber);
        setSuccess(`Contact ${contactName || phoneNumber} unpaused successfully`);
        loadCampaignData();
      } catch (error) {
        setError('Failed to unpause contact');
      }
    }
  };

  const handleRemoveContact = async (contactId, contactName) => {
    if (window.confirm(`Remove ${contactName || 'this contact'} from the campaign?\n\nThis will not delete the contact from your database, only remove them from this campaign.`)) {
      try {
        await campaignAPI.removeContact(id, contactId);
        setSuccess(`Contact removed from campaign`);
        loadCampaignData();
      } catch (error) {
        setError('Failed to remove contact');
      }
    }
  };

  const loadFailedMessages = async () => {
    try {
      const response = await campaignAPI.getFailedMessages(id);
      setFailedMessages(response.data);
    } catch (error) {
      console.error('Error loading failed messages:', error);
      setError('Failed to load failed messages');
    }
  };

  const handleRetryFailed = async (messageLogIds = null) => {
    const confirmMessage = messageLogIds 
      ? 'Retry this failed message?' 
      : `Retry all ${failedMessages.length} failed messages?`;
    
    if (window.confirm(confirmMessage)) {
      try {
        const response = await campaignAPI.retryFailed(id, messageLogIds);
        setSuccess(`✅ ${response.data.retried} message(s) queued for retry! Check Monitoring tab for progress.`);
        loadFailedMessages();
        loadCampaignData(); // Reload stats
      } catch (error) {
        setError('Failed to retry messages: ' + (error.response?.data?.error || error.message));
      }
    }
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    try {
      await campaignAPI.saveConfig(id, config);
      setSuccess('Configuration saved successfully');
    } catch (error) {
      setError('Failed to save configuration');
    }
  };

  const toggleContactSelection = (contactId) => {
    setSelectedContacts(prev =>
      prev.includes(contactId)
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  const handleSelectAllContacts = (availableContacts) => {
    const availableIds = availableContacts.map(c => c.id);
    const allSelected = availableIds.every(id => selectedContacts.includes(id));
    
    if (allSelected) {
      // Deselect all
      setSelectedContacts(prev => prev.filter(id => !availableIds.includes(id)));
    } else {
      // Select all
      setSelectedContacts(prev => {
        const newIds = availableIds.filter(id => !prev.includes(id));
        return [...prev, ...newIds];
      });
    }
  };

  if (loading) {
    return <div className="container"><div className="loading">Loading campaign...</div></div>;
  }

  if (!campaign) {
    return <div className="container"><div className="error">Campaign not found</div></div>;
  }

  return (
    <div className="container">
      <button className="btn btn-secondary" onClick={() => navigate('/campaigns')} style={{ marginBottom: '20px' }}>
        ← Back to Campaigns
      </button>

      <h1>{campaign.name}</h1>
      <p>{campaign.description}</p>
      
      {/* WhatsApp Connection Status */}
      <div style={{ 
        padding: '10px 15px', 
        backgroundColor: whatsappStatus.isConnected ? '#d4edda' : '#f8d7da',
        border: `1px solid ${whatsappStatus.isConnected ? '#c3e6cb' : '#f5c6cb'}`,
        borderRadius: '4px',
        marginBottom: '15px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        <span style={{ fontSize: '20px' }}>
          {whatsappStatus.isConnected ? '✅' : whatsappStatus.state === 'connecting' ? '🔄' : '❌'}
        </span>
        <div>
          <strong>WhatsApp Status:</strong> 
          {whatsappStatus.isConnected ? ' Connected' : 
           whatsappStatus.state === 'qr' ? ' Scan QR Code to Connect' :
           whatsappStatus.state === 'connecting' ? ' Connecting...' : ' Disconnected'}
          {whatsappStatus.state === 'qr' && (
            <div style={{ fontSize: '12px', marginTop: '4px' }}>
              Check server terminal for QR code or visit WhatsApp settings page
            </div>
          )}
        </div>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      <div className="grid">
        <div className="stat-card">
          <h3>Total Messages</h3>
          <div className="stat-value">{stats?.total_messages || 0}</div>
        </div>
        <div className="stat-card">
          <h3>Sent</h3>
          <div className="stat-value" style={{ color: '#25d366' }}>{stats?.sent || 0}</div>
        </div>
        <div className="stat-card">
          <h3>Failed</h3>
          <div className="stat-value" style={{ color: '#dc3545' }}>{stats?.failed || 0}</div>
        </div>
        <div className="stat-card">
          <h3>Paused (Replied to This)</h3>
          <div className="stat-value" style={{ color: '#ffc107' }}>{stats?.paused || 0}</div>
        </div>
        <div className="stat-card">
          <h3>Contacts</h3>
          <div className="stat-value">{contacts.length}</div>
        </div>
      </div>

      <div className="button-group" style={{ marginBottom: '20px' }}>
        <button 
          className="btn btn-primary" 
          onClick={handleSendNow}
          disabled={!whatsappStatus.isConnected}
          title={!whatsappStatus.isConnected ? 'WhatsApp must be connected to queue messages' : ''}
        >
          📤 Queue Campaign
        </button>
        <button className="btn btn-primary" onClick={() => setShowScheduleModal(true)}>
          ⏰ Schedule
        </button>
      </div>
      
      <div style={{ 
        padding: '12px', 
        backgroundColor: '#e3f2fd', 
        borderRadius: '4px', 
        marginBottom: '20px',
        fontSize: '14px'
      }}>
        💡 <strong>Smart Sending:</strong> Messages use randomized delays (30-90s), simulated typing, 
        message template rotation, and auto-pause on replies to avoid detection. 
        <br/><small>• Multiple templates = different contacts get different messages (rotation avoids broadcast detection)</small>
        <br/><small>• Pausing is campaign-specific - contacts who reply to this campaign can still receive messages from other campaigns.</small>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'messages' ? 'active' : ''}`}
          onClick={() => setActiveTab('messages')}
        >
          Messages ({messages.length})
        </button>
        <button 
          className={`tab ${activeTab === 'contacts' ? 'active' : ''}`}
          onClick={() => setActiveTab('contacts')}
        >
          Contacts ({contacts.length})
        </button>
        <button 
          className={`tab ${activeTab === 'failed' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('failed');
            loadFailedMessages();
          }}
        >
          Failed Messages ({stats?.failed || 0})
        </button>
        <button 
          className={`tab ${activeTab === 'config' ? 'active' : ''}`}
          onClick={() => setActiveTab('config')}
        >
          Configuration
        </button>
      </div>

      {/* Messages Tab */}
      {activeTab === 'messages' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h2>Messages ({messages.length})</h2>
            <button className="btn btn-primary" onClick={() => {
              setEditingMessageId(null);
              setMessageForm({ content: '', template_name: '', media_url: '', sequence_order: 0, delay_hours: 0 });
              setShowMessageModal(true);
            }}>
              Add Message
            </button>
          </div>
          
          {config.is_drip_campaign === 1 && (
            <div style={{ 
              padding: '10px 15px', 
              backgroundColor: '#e3f2fd', 
              border: '1px solid #2196f3',
              borderRadius: '4px', 
              marginBottom: '15px',
              fontSize: '13px'
            }}>
              📧 <strong>Drip Campaign Mode:</strong> Messages will be sent in sequence with delays. 
              Set <strong>Sequence Order</strong> (0=Initial, 1=Follow-up 1, 2=Follow-up 2, etc.) and <strong>Delay Hours</strong> (hours to wait before sending this message).
              <br />
              <strong>💡 Pro Tip:</strong> Add multiple messages with the same sequence order (e.g., 5 messages all with sequence 0) - one will be randomly selected for each contact to avoid detection!
              <br />
              Follow-ups stop if contact replies.
            </div>
          )}
          
          {config.is_drip_campaign === 0 && messages.length > 1 && (
            <div style={{ 
              padding: '10px 15px', 
              backgroundColor: '#fff3cd', 
              border: '1px solid #ffc107',
              borderRadius: '4px', 
              marginBottom: '15px',
              fontSize: '13px'
            }}>
              🔄 <strong>Random Message Selection Active:</strong> Each contact will receive a randomly selected message from your {messages.length} templates to avoid Meta detection. 
              The more message variations you add, the better!
            </div>
          )}
          
          {messages.length === 0 ? (
            <p>No messages yet. Add a message to get started.</p>
          ) : (
            <div className="table-container">
              <table className="table">
              <thead>
                <tr>
                  <th>{config.is_drip_campaign === 1 ? 'Sequence' : '#'}</th>
                  <th>Template Name</th>
                  <th>Content</th>
                  {config.is_drip_campaign === 1 && <th>Delay (Hours)</th>}
                  <th>Media</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {[...messages]
                  .sort((a, b) => config.is_drip_campaign === 1 ? (a.sequence_order || 0) - (b.sequence_order || 0) : 0)
                  .map((message, index) => (
                  <tr key={message.id}>
                    <td>
                      {config.is_drip_campaign === 1 ? (
                        <strong>
                          {message.sequence_order === 0 ? 'Initial' : `Follow-up ${message.sequence_order}`}
                        </strong>
                      ) : (
                        <strong>Template {index + 1}</strong>
                      )}
                    </td>
                    <td>{message.template_name || '-'}</td>
                    <td>{message.content.substring(0, 50)}...</td>
                    {config.is_drip_campaign === 1 && (
                      <td>
                        {message.sequence_order === 0 ? (
                          <span style={{ color: '#666' }}>Immediate</span>
                        ) : (
                          <span>{message.delay_hours || 0}h</span>
                        )}
                      </td>
                    )}
                    <td>{message.media_url ? 'Yes' : 'No'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '5px' }}>
                        <button 
                          className="btn btn-secondary" 
                          onClick={() => handleEditMessage(message)}
                          style={{ padding: '5px 10px', fontSize: '14px' }}
                        >
                          Edit
                        </button>
                        <button 
                          className="btn btn-danger" 
                          onClick={() => handleDeleteMessage(message.id)}
                          style={{ padding: '5px 10px', fontSize: '14px' }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}

      {/* Contacts Tab */}
      {activeTab === 'contacts' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h2>Contacts ({contacts.length})</h2>
            <button className="btn btn-primary" onClick={() => setShowContactModal(true)}>
              Add Contacts
            </button>
          </div>
          {contacts.length === 0 ? (
            <p>No contacts yet. Add contacts to send messages.</p>
          ) : (
            <div className="table-container">
              <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone Number</th>
                  <th>Tags</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map(contact => (
                  <tr key={contact.id} style={{ opacity: contact.is_paused ? 0.6 : 1 }}>
                    <td>{contact.name || '-'}</td>
                    <td>{contact.phone_number}</td>
                    <td>{contact.tags || '-'}</td>
                    <td>
                      {contact.is_paused ? (
                        <span style={{ 
                          backgroundColor: '#ffc107', 
                          color: '#000', 
                          padding: '4px 8px', 
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}>
                          ⏸️ PAUSED (Replied to This Campaign)
                        </span>
                      ) : (
                        <span style={{ color: '#28a745' }}>✓ Active</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        {contact.is_paused === 1 && (
                          <button 
                            className="btn btn-primary"
                            onClick={() => handleUnpauseContact(contact.phone_number, contact.name)}
                            style={{ padding: '5px 10px', fontSize: '14px' }}
                          >
                            Resume Messaging
                          </button>
                        )}
                        <span 
                          onClick={() => handleRemoveContact(contact.id, contact.name)}
                          style={{ 
                            fontSize: '20px', 
                            cursor: 'pointer', 
                            color: '#dc3545',
                            transition: 'transform 0.2s'
                          }}
                          title="Remove from campaign"
                          onMouseEnter={(e) => e.target.style.transform = 'scale(1.2)'}
                          onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                        >
                          🗑️
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}

      {/* Configuration Tab */}
      {activeTab === 'config' && (
        <div className="card">
          <h2>Campaign Configuration</h2>
          <form onSubmit={handleSaveConfig}>
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={config.is_drip_campaign === 1}
                  onChange={(e) => setConfig({ ...config, is_drip_campaign: e.target.checked ? 1 : 0 })}
                  style={{ marginRight: '10px' }}
                />
                <span style={{ fontWeight: 'bold' }}>Enable Drip Campaign (Follow-up Sequences)</span>
              </label>
              <small style={{ color: '#666', marginLeft: '28px', display: 'block', marginTop: '5px' }}>
                Send sequential follow-up messages with delays. Messages will only be sent if contact doesn't reply.
              </small>
            </div>

            {config.is_drip_campaign === 1 && (
              <div style={{ 
                padding: '15px', 
                backgroundColor: '#e3f2fd', 
                borderRadius: '5px', 
                marginBottom: '20px',
                border: '1px solid #90caf9'
              }}>
                <strong style={{ color: '#1976d2' }}>ℹ️ Drip Campaign Mode</strong>
                <p style={{ margin: '10px 0 0 0', fontSize: '14px', color: '#333' }}>
                  In the Messages tab, set the <strong>Sequence Order</strong> (0, 1, 2...) for each message.
                  <br />
                  Use the <strong>Sequence Delays</strong> field below to control timing between sequences.
                  <br />
                  <br />
                  <strong>🎯 Anti-Detection Feature:</strong> You can add multiple messages with the same sequence order!
                  <br />
                  For example, create 5 different messages all with sequence 0 - the system will randomly pick one for each contact.
                  This makes your campaign look more natural and avoids Meta's spam detection.
                  <br />
                  <br />
                  <strong>If contact replies at any point, all future messages stop automatically.</strong>
                </p>
              </div>
            )}

            {config.is_drip_campaign === 1 && (
              <div className="form-group">
                <label>Sequence Delays (hours)</label>
                <input
                  type="text"
                  value={config.sequence_delays || '24,48,72'}
                  onChange={(e) => setConfig({ ...config, sequence_delays: e.target.value })}
                  placeholder="24,48,72"
                />
                <small style={{ color: '#666' }}>
                  Comma-separated hours for delays between sequences. Example: "24,48,72" means 24h after initial message before follow-up 1, 48h after follow-up 1 before follow-up 2, etc.
                </small>
              </div>
            )}

            <div className="form-group">
              <label>Messages Per Day</label>
              <input
                type="number"
                min="1"
                max="1000"
                value={config.messages_per_day}
                onChange={(e) => setConfig({ ...config, messages_per_day: parseInt(e.target.value) })}
              />
              <small style={{ color: '#666' }}>Maximum number of messages to send per day</small>
            </div>

            <div className="form-group">
              <label>Delay Between Messages (milliseconds) - Deprecated</label>
              <input
                type="number"
                min="1000"
                max="60000"
                step="1000"
                value={config.delay_between_messages}
                onChange={(e) => setConfig({ ...config, delay_between_messages: parseInt(e.target.value) })}
                disabled
                style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
              />
              <small style={{ color: '#ff9800', fontWeight: 'bold' }}>
                ⚠️ This setting is now deprecated. Messages are sent with random delays (45 seconds to 3 minutes) for better anti-detection.
              </small>
            </div>

            <div className="form-group">
              <label>Start Time</label>
              <input
                type="time"
                value={config.start_time}
                onChange={(e) => setConfig({ ...config, start_time: e.target.value })}
              />
              <small style={{ color: '#666' }}>Messages will only be sent after this time</small>
            </div>

            <div className="form-group">
              <label>End Time</label>
              <input
                type="time"
                value={config.end_time}
                onChange={(e) => setConfig({ ...config, end_time: e.target.value })}
              />
              <small style={{ color: '#666' }}>Messages will only be sent before this time</small>
            </div>

            <div className="form-group">
              <label>Excluded Days</label>
              <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginTop: '10px' }}>
                {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => {
                  const excludedDays = config.excluded_days.split(',').map(d => d.trim());
                  const isExcluded = excludedDays.includes(index.toString());
                  
                  return (
                    <label key={index} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={isExcluded}
                        onChange={(e) => {
                          let days = excludedDays.filter(d => d !== '');
                          if (e.target.checked) {
                            days.push(index.toString());
                          } else {
                            days = days.filter(d => d !== index.toString());
                          }
                          setConfig({ ...config, excluded_days: days.join(',') });
                        }}
                        style={{ marginRight: '5px' }}
                      />
                      {day}
                    </label>
                  );
                })}
              </div>
              <small style={{ color: '#666' }}>Select days when messages should NOT be sent</small>
            </div>

            <button type="submit" className="btn btn-primary">
              Save Configuration
            </button>
          </form>
        </div>
      )}

      {/* Failed Messages Tab */}
      {activeTab === 'failed' && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h2>Failed Messages ({failedMessages.length})</h2>
            {failedMessages.length > 0 && (
              <button 
                className="btn btn-primary" 
                onClick={() => handleRetryFailed(null)}
                disabled={!whatsappStatus.isConnected}
                title={!whatsappStatus.isConnected ? 'WhatsApp must be connected to retry messages' : ''}
              >
                🔄 Retry All Failed
              </button>
            )}
          </div>

          {failedMessages.length === 0 ? (
            <div style={{ 
              padding: '40px', 
              textAlign: 'center', 
              color: '#666',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '10px' }}>✅</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold' }}>No Failed Messages</div>
              <div style={{ fontSize: '14px', marginTop: '5px' }}>All messages were sent successfully!</div>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
              <thead>
                <tr>
                  <th>Contact</th>
                  <th>Phone Number</th>
                  <th>Error Message</th>
                  <th>Failed At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {failedMessages.map(msg => (
                  <tr key={msg.id}>
                    <td>{msg.contact_name || '-'}</td>
                    <td>{msg.phone_number}</td>
                    <td style={{ color: '#dc3545', fontSize: '13px' }}>
                      {msg.error_message || 'Unknown error'}
                    </td>
                    <td style={{ fontSize: '13px' }}>
                      {msg.sent_at ? new Date(msg.sent_at).toLocaleString() : '-'}
                    </td>
                    <td>
                      <button 
                        className="btn btn-primary"
                        onClick={() => handleRetryFailed([msg.id])}
                        style={{ padding: '5px 10px', fontSize: '14px' }}
                        disabled={!whatsappStatus.isConnected}
                        title={!whatsappStatus.isConnected ? 'WhatsApp must be connected to retry' : 'Retry this message'}
                      >
                        🔄 Retry
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}

          <div style={{ 
            marginTop: '20px',
            padding: '15px', 
            backgroundColor: '#fff3cd', 
            borderRadius: '4px',
            fontSize: '14px',
            border: '1px solid #ffc107'
          }}>
            <strong>💡 About Failed Messages:</strong>
            <ul style={{ marginTop: '10px', marginBottom: '0', paddingLeft: '20px' }}>
              <li>Messages can fail due to invalid numbers, WhatsApp disconnection, or network issues</li>
              <li>Retry will re-queue the message with the same delays as original campaign</li>
              <li>Make sure WhatsApp is connected before retrying</li>
              <li>Check the error message to understand why the message failed</li>
            </ul>
          </div>
        </div>
      )}

      {showMessageModal && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editingMessageId ? 'Edit Message' : 'Add Message'}</h2>
              <button className="close-btn" onClick={handleCloseMessageModal}>&times;</button>
            </div>
            <form onSubmit={handleAddMessage}>
              {/* Show template selector for Business API */}
              {whatsappStatus.type === 'business_api' && whatsappStatus.hasApiKey && (
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>Select Approved Template</span>
                    {loadingTemplates && <small style={{ color: '#666' }}>Loading templates...</small>}
                  </label>
                  <select
                    value={messageForm.template_id}
                    onChange={handleTemplateSelect}
                    style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
                  >
                    <option value="">-- Select a WhatsApp approved template --</option>
                    {businessTemplates.map(template => (
                      <option key={template._id} value={template._id}>
                        {template.name} - {template.language} ({template.whatsappStatus || template.status})
                      </option>
                    ))}
                  </select>
                  <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                    📋 Business API requires approved templates. Select one to auto-fill the message content.
                  </small>
                </div>
              )}
              
              {/* Show placeholder inputs when template is selected */}
              {templatePlaceholders.length > 0 && (
                <div className="form-group" style={{ background: '#f0f9ff', padding: '20px', borderRadius: '8px', border: '2px solid #0ea5e9' }}>
                  <label style={{ marginBottom: '5px', display: 'block', fontSize: '16px', fontWeight: 'bold', color: '#0c4a6e' }}>
                    📝 Fill Template Variables
                  </label>
                  <div style={{ fontSize: '13px', color: '#0369a1', marginBottom: '20px', padding: '8px', background: '#e0f2fe', borderRadius: '4px' }}>
                    <strong>{selectedTemplate?.name}</strong>
                  </div>
                  
                  {templatePlaceholders.map((num) => (
                    <div key={num} style={{ marginBottom: '20px', padding: '15px', background: 'white', borderRadius: '6px', border: '1px solid #cbd5e1' }}>
                      <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', display: 'block', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Variable {num} {'{' + '{' + num + '}' + '}'}
                      </label>
                      <input
                        type="text"
                        value={placeholderValues[num] || ''}
                        onChange={(e) => setPlaceholderValues({ ...placeholderValues, [num]: e.target.value })}
                        placeholder={`Enter value for {{${num}}}`}
                        style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #cbd5e1', marginBottom: '10px', fontSize: '14px' }}
                      />
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={() => setPlaceholderValues({ ...placeholderValues, [num]: '{{name}}' })}
                          style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid #cbd5e1', borderRadius: '20px', background: '#f8fafc', cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >
                          Full Name · {'{' + '{name}' + '}'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setPlaceholderValues({ ...placeholderValues, [num]: '{{first_name}}' })}
                          style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid #cbd5e1', borderRadius: '20px', background: '#f8fafc', cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >
                          First Name · {'{' + '{first_name}' + '}'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setPlaceholderValues({ ...placeholderValues, [num]: '{{last_name}}' })}
                          style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid #cbd5e1', borderRadius: '20px', background: '#f8fafc', cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >
                          Last Name · {'{' + '{last_name}' + '}'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setPlaceholderValues({ ...placeholderValues, [num]: '{{phone}}' })}
                          style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid #cbd5e1', borderRadius: '20px', background: '#f8fafc', cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >
                          Phone · {'{' + '{phone}' + '}'}
                        </button>
                      </div>
                    </div>
                  ))}
                  
                  <div style={{ marginTop: '20px', padding: '15px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #cbd5e1' }}>
                    <label style={{ fontSize: '13px', fontWeight: '600', marginBottom: '10px', display: 'block', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Preview
                    </label>
                    <div style={{ background: '#dcfce7', padding: '15px', borderRadius: '8px', fontSize: '14px', lineHeight: '1.6', color: '#166534', whiteSpace: 'pre-wrap' }}>
                      {(() => {
                        let preview = messageForm.content;
                        templatePlaceholders.forEach(num => {
                          const value = placeholderValues[num] || `{{${num}}}`;
                          preview = preview.replace(new RegExp(`\\{\\{${num}\\}\\}`, 'g'), value);
                        });
                        return preview;
                      })()}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="form-group">
                <label>Template Name</label>
                <input
                  type="text"
                  value={messageForm.template_name}
                  onChange={(e) => setMessageForm({ ...messageForm, template_name: e.target.value })}
                  placeholder="Optional template name"
                  readOnly={whatsappStatus.type === 'business_api' && messageForm.template_id}
                />
              </div>
              
              {config.is_drip_campaign === 1 && (
                <>
                  <div className="form-group">
                    <label>Sequence Order *</label>
                    <input
                      type="number"
                      min="0"
                      required
                      value={messageForm.sequence_order}
                      onChange={(e) => setMessageForm({ ...messageForm, sequence_order: parseInt(e.target.value) })}
                      placeholder="0 for initial, 1 for first follow-up, etc."
                    />
                    <small style={{ color: '#666' }}>
                      0 = Initial message, 1 = First follow-up, 2 = Second follow-up, etc.<br />
                      <strong>💡 Tip:</strong> You can add multiple messages with the same sequence order - one will be randomly selected per contact!
                    </small>
                  </div>
                  
                  <div className="form-group">
                    <label>Delay Hours {messageForm.sequence_order === 0 ? '(ignored for initial)' : '*'}</label>
                    <input
                      type="number"
                      min="0"
                      required={messageForm.sequence_order > 0}
                      value={messageForm.delay_hours}
                      onChange={(e) => setMessageForm({ ...messageForm, delay_hours: parseInt(e.target.value) })}
                      placeholder="Hours to wait before sending this message"
                    />
                    <small style={{ color: '#666' }}>
                      {messageForm.sequence_order === 0 
                        ? 'Initial message is sent immediately (delay not used)' 
                        : `Wait ${messageForm.delay_hours || 0} hours after previous message before sending`}
                    </small>
                  </div>
                </>
              )}
              
              <div className="form-group">
                <label>Message Content *</label>
                <textarea
                  required
                  value={messageForm.content}
                  onChange={(e) => setMessageForm({ ...messageForm, content: e.target.value })}
                  placeholder="Enter your message here..."
                  style={{ minHeight: '120px' }}
                  readOnly={whatsappStatus.type === 'business_api' && messageForm.template_id}
                />
                <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                  {whatsappStatus.type === 'business_api' ? (
                    messageForm.template_id ? (
                      <>
                        📋 <strong>Template content (read-only):</strong> This is the approved template content from WhatsApp Business API.
                      </>
                    ) : (
                      <>
                        ⚠️ <strong>Business API mode:</strong> Please select an approved template above.
                      </>
                    )
                  ) : (
                    <>
                      <strong>Spintax:</strong> {'{Hi|Hello|Hey}'} chooses random option<br/>
                      <strong>Variables:</strong> Use name, first_name, or phone in double curly braces<br/>
                      <strong>Note:</strong> name = smart first name (skips initials like "N. Rangaswamy" → "Rangaswamy")
                    </>
                  )}
                </small>
              </div>
              <div className="form-group">
                <label>Media URL (optional)</label>
                <input
                  type="url"
                  value={messageForm.media_url}
                  onChange={(e) => setMessageForm({ ...messageForm, media_url: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                />
              </div>
              <div className="button-group">
                <button type="submit" className="btn btn-primary">
                  {editingMessageId ? 'Update Message' : 'Add Message'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={handleCloseMessageModal}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showContactModal && (() => {
        let availableContacts = allContacts.filter(c => !contacts.find(ec => ec.id === c.id));
        
        // Apply "New Contacts Only" filter
        if (showNewContactsOnly) {
          availableContacts = availableContacts.filter(c => c.has_been_contacted === 0);
        }
        
        // Apply search filter
        if (contactSearchText.trim()) {
          const searchLower = contactSearchText.toLowerCase();
          availableContacts = availableContacts.filter(c => 
            c.name?.toLowerCase().includes(searchLower) ||
            c.phone_number?.includes(contactSearchText) ||
            c.tags?.toLowerCase().includes(searchLower)
          );
        }
        
        const availableIds = availableContacts.map(c => c.id);
        const allSelected = availableIds.length > 0 && availableIds.every(id => selectedContacts.includes(id));
        
        return (
          <div className="modal">
            <div className="modal-content">
              <div className="modal-header">
                <h2>Add Contacts to Campaign</h2>
                <button className="close-btn" onClick={() => {
                  setShowContactModal(false);
                  setContactSearchText('');
                }}>&times;</button>
              </div>
              
              <div style={{ 
                padding: '15px', 
                borderBottom: '1px solid #e0e0e0',
                backgroundColor: '#fff'
              }}>
                <input
                  type="text"
                  placeholder="🔍 Search by name, phone, or tags..."
                  value={contactSearchText}
                  onChange={(e) => setContactSearchText(e.target.value)}
                  style={{ 
                    width: '100%', 
                    padding: '10px 12px', 
                    border: '1px solid #ddd', 
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>
              
              <div style={{ 
                padding: '15px', 
                borderBottom: '1px solid #e0e0e0',
                backgroundColor: '#fff'
              }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={showNewContactsOnly}
                    onChange={(e) => setShowNewContactsOnly(e.target.checked)}
                    style={{ marginRight: '10px', width: '18px', height: '18px' }}
                  />
                  <span>Show New Contacts Only (never contacted before)</span>
                </label>
              </div>
              
              {availableContacts.length > 0 && (
                <div style={{ 
                  padding: '15px', 
                  borderBottom: '2px solid #e0e0e0',
                  backgroundColor: '#f5f5f5'
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 'bold' }}>
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={() => handleSelectAllContacts(availableContacts)}
                      style={{ marginRight: '10px', width: '18px', height: '18px' }}
                    />
                    Select All ({availableContacts.length} contacts)
                  </label>
                </div>
              )}
              
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {availableContacts.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                    {contactSearchText.trim() ? (
                      <>
                        <p style={{ marginBottom: '5px' }}>No contacts match your search.</p>
                        <p style={{ fontSize: '14px' }}>Try different keywords or clear the search.</p>
                      </>
                    ) : showNewContactsOnly ? (
                      <p>No new contacts available. All contacts have been contacted before.</p>
                    ) : (
                      <p>All contacts have already been added to this campaign.</p>
                    )}
                  </div>
                ) : (
                  availableContacts.map(contact => (
                <div key={contact.id} style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={selectedContacts.includes(contact.id)}
                      onChange={() => toggleContactSelection(contact.id)}
                      style={{ marginRight: '10px' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <strong>{contact.name || 'Unknown'}</strong>
                        {contact.has_been_contacted === 0 && (
                          <span style={{
                            fontSize: '11px',
                            padding: '2px 8px',
                            backgroundColor: '#4CAF50',
                            color: 'white',
                            borderRadius: '10px',
                            fontWeight: 'bold'
                          }}>NEW</span>
                        )}
                      </div>
                      <div style={{ fontSize: '14px', color: '#666' }}>{contact.phone_number}</div>
                      {contact.tags && (
                        <div style={{ fontSize: '12px', color: '#999', marginTop: '2px' }}>
                          🏷️ {contact.tags}
                        </div>
                      )}
                    </div>
                  </label>
                </div>
                  ))
                )}
              </div>
              <div className="button-group" style={{ marginTop: '15px' }}>
                <button 
                  className="btn btn-primary" 
                  onClick={handleAddContacts}
                  disabled={selectedContacts.length === 0}
                >
                  Add Selected ({selectedContacts.length})
                </button>
                <button className="btn btn-secondary" onClick={() => {
                  setShowContactModal(false);
                  setContactSearchText('');
                  setShowNewContactsOnly(false);
                }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {showScheduleModal && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Schedule Campaign</h2>
              <button className="close-btn" onClick={() => setShowScheduleModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSchedule}>
              <div className="form-group">
                <label>Schedule Date & Time *</label>
                <input
                  type="datetime-local"
                  required
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                  style={{ fontSize: '16px' }}
                />
                <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                  Select when you want this campaign to be sent
                </small>
              </div>
              <div className="button-group">
                <button type="submit" className="btn btn-primary">Schedule</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowScheduleModal(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CampaignDetail;
